import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { tagLabel } from '../../helpers/trainerNotifications'
import { RADIUS, TEXT, TRAINER_STRIP } from '../../theme/tokens'

/**
 * The notification as the trainer dashboard's strip shows it: the counter
 * pill, the tag pill, the message and the CTA button. A preview only; the
 * strip itself lives in trainer-ui.
 */
const StripPreview = ({ tag, message, ctaLabel }) => {
    return (
        <Box>
            <Typography
                variant="overline"
                component="div"
                sx={{ color: TEXT.muted, lineHeight: 2, mb: 0.5 }}
            >
                Preview
            </Typography>
            <Box
                data-testid="strip-preview"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'wrap',
                    background: TRAINER_STRIP.bar,
                    borderRadius: `${RADIUS.sm}px`,
                    px: 1.5,
                    py: 1.25,
                    minHeight: 52,
                }}
            >
                <Box
                    component="span"
                    sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: `${RADIUS.sm}px`,
                        typography: 'caption',
                        fontWeight: 700,
                        color: TRAINER_STRIP.text,
                        background: TRAINER_STRIP.counter,
                    }}
                >
                    1/1
                </Box>
                {/* As the dashboard draws every tag: a white pill with a
                    coloured dot, not an icon. */}
                {tag && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.25,
                            borderRadius: `${RADIUS.sm}px`,
                            typography: 'caption',
                            fontWeight: 700,
                            color: TRAINER_STRIP.pillText,
                            background: TRAINER_STRIP.pill,
                        }}
                    >
                        <Box
                            component="span"
                            aria-hidden
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                flexShrink: 0,
                                background:
                                    TRAINER_STRIP.tagDot[tag] ??
                                    TRAINER_STRIP.tagDotFallback,
                            }}
                        />
                        {tagLabel(tag)}
                    </Box>
                )}
                <Typography
                    sx={{
                        flex: '1 1 160px',
                        minWidth: 0,
                        typography: 'body2',
                        fontWeight: 600,
                        color: TRAINER_STRIP.text,
                        wordBreak: 'break-word',
                    }}
                >
                    {message || 'Your message appears here'}
                </Typography>
                {ctaLabel && (
                    <Box
                        component="span"
                        sx={{
                            px: 1.25,
                            py: 0.4,
                            borderRadius: `${RADIUS.sm}px`,
                            border: `1px solid ${TRAINER_STRIP.ctaBorder}`,
                            typography: 'caption',
                            fontWeight: 700,
                            color: TRAINER_STRIP.text,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {ctaLabel}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

StripPreview.propTypes = {
    tag: PropTypes.string,
    message: PropTypes.string,
    ctaLabel: PropTypes.string,
}

export default StripPreview
